import React, { useState, useEffect } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  UserPlus, Mail, Copy, Trash2, RefreshCw, CheckCircle, Clock, 
  Shield, Crown, Briefcase, User, AlertCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvitesPage = () => {
  const { user: currentUser, token } = useAuth();
  const { t } = useLanguage();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteRole, setNewInviteRole] = useState('user');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchInvites();
  }, [token]);

  const fetchInvites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invites`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setInvites(data);
      }
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!newInviteEmail) {
      toast.error('Email is required');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/invites`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          email: newInviteEmail,
          role: newInviteRole
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invite sent to ${newInviteEmail}`);
        setCreateDialogOpen(false);
        setNewInviteEmail('');
        setNewInviteRole('user');
        fetchInvites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create invite');
      }
    } catch (error) {
      toast.error('Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!confirm('Are you sure you want to cancel this invite?')) return;

    try {
      const response = await fetch(`${API_URL}/api/invites/${inviteId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        toast.success('Invite cancelled');
        fetchInvites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel invite');
      }
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/invites/${inviteId}/resend`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        toast.success('Invite resent successfully');
        fetchInvites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to resend invite');
      }
    } catch (error) {
      toast.error('Failed to resend invite');
    }
  };

  const copyInviteLink = (inviteCode) => {
    const link = `${window.location.origin}/register?invite=${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard');
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Crown className="w-4 h-4" />;
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'manager': return <Briefcase className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'text-yellow-400 bg-yellow-400/10';
      case 'admin': return 'text-primary bg-primary/10';
      case 'manager': return 'text-blue-400 bg-blue-400/10';
      default: return 'text-white/60 bg-white/10';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const pendingInvites = invites.filter(i => !i.used && !isExpired(i.expires_at));
  const usedInvites = invites.filter(i => i.used);
  const expiredInvites = invites.filter(i => !i.used && isExpired(i.expires_at));

  return (
    <DashboardLayout title="Invite Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div>
            <p className="text-white/60">
              Invite new users to join your private document vault. Only invited users can register.
            </p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider btn-glow" data-testid="create-invite-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Invite New User
                </DialogTitle>
                <DialogDescription className="text-white/60">
                  Send an invitation to join your secure document vault.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white/80">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      type="email"
                      value={newInviteEmail}
                      onChange={(e) => setNewInviteEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="invite-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white/80">Role</Label>
                  <Select value={newInviteRole} onValueChange={setNewInviteRole}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="user" className="text-white">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-white/60" />
                          User - Basic access
                        </div>
                      </SelectItem>
                      <SelectItem value="manager" className="text-white">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-blue-400" />
                          Manager - Team sharing
                        </div>
                      </SelectItem>
                      {currentUser?.role === 'super_admin' && (
                        <>
                          <SelectItem value="admin" className="text-white">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-primary" />
                              Admin - User management
                            </div>
                          </SelectItem>
                          <SelectItem value="super_admin" className="text-white">
                            <div className="flex items-center gap-2">
                              <Crown className="w-4 h-4 text-yellow-400" />
                              Super Admin - Full access
                            </div>
                          </SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setCreateDialogOpen(false)}
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleCreateInvite}
                    disabled={creating || !newInviteEmail}
                    className="flex-1 bg-primary text-black hover:bg-primary/90"
                    data-testid="send-invite-btn"
                  >
                    {creating ? t('common.loading') : 'Send Invite'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Banner */}
        <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium">Private Registration Mode</p>
            <p className="text-white/60 text-sm">
              Public registration is disabled. Only users with a valid invite code can create accounts.
              Invites expire after 7 days.
            </p>
          </div>
        </div>

        {/* Pending Invites */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            Pending Invites ({pendingInvites.length})
          </h3>
          
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-black/40 border border-white/10 rounded-sm p-4 h-20" />
              ))}
            </div>
          ) : pendingInvites.length === 0 ? (
            <div className="text-center py-8 bg-black/40 border border-white/10 rounded-sm">
              <UserPlus className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/60">No pending invites</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingInvites.map((invite) => (
                <div 
                  key={invite.invite_id}
                  className="bg-black/40 border border-white/10 rounded-sm p-4 hover:border-white/20 transition-colors"
                  data-testid={`invite-${invite.invite_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-yellow-500" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{invite.email}</p>
                        <div className="flex items-center gap-3 text-xs text-white/40">
                          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-sm ${getRoleColor(invite.role)}`}>
                            {getRoleIcon(invite.role)}
                            {invite.role.replace('_', ' ')}
                          </span>
                          <span>Expires: {formatDate(invite.expires_at)}</span>
                          <span>By: {invite.invited_by_name}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyInviteLink(invite.invite_code)}
                        className="text-white/60 hover:text-white hover:bg-white/5"
                        title="Copy invite link"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResendInvite(invite.invite_id)}
                        className="text-white/60 hover:text-white hover:bg-white/5"
                        title="Resend invite"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteInvite(invite.invite_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                        title="Cancel invite"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Used Invites */}
        {usedInvites.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              Accepted Invites ({usedInvites.length})
            </h3>
            
            <div className="space-y-3">
              {usedInvites.map((invite) => (
                <div 
                  key={invite.invite_id}
                  className="bg-black/40 border border-primary/20 rounded-sm p-4"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{invite.email}</p>
                      <div className="flex items-center gap-3 text-xs text-white/40">
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-sm ${getRoleColor(invite.role)}`}>
                          {getRoleIcon(invite.role)}
                          {invite.role.replace('_', ' ')}
                        </span>
                        <span>Registered</span>
                        <span>Invited by: {invite.invited_by_name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Expired Invites */}
        {expiredInvites.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Expired Invites ({expiredInvites.length})
            </h3>
            
            <div className="space-y-3 opacity-60">
              {expiredInvites.map((invite) => (
                <div 
                  key={invite.invite_id}
                  className="bg-black/40 border border-red-500/20 rounded-sm p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{invite.email}</p>
                        <div className="flex items-center gap-3 text-xs text-white/40">
                          <span>Expired: {formatDate(invite.expires_at)}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteInvite(invite.invite_id)}
                      className="text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InvitesPage;
